import React, { useEffect, useState, useCallback, useRef } from 'react';
import { RefreshCw, CloudOff, AlertOctagon } from 'lucide-react';
import { resumenCola, EVENTO_COLA } from 'lib/offlineQueue';
import { flushQueue, reintentarTrabajo } from 'services/inspeccionService';
import { ESTADO_PILL } from 'lib/estadoUi';

// Inspecciones guardadas en este teléfono que todavía no llegaron a la base.
// Se ve en el asistente, en la pantalla final y en la de PIN: una inspección
// que se queda en un teléfono sin que nadie lo note es una inspección perdida.
// Las rechazadas por la base (permisos, CHECK, fecha) quedan aparte, en rojo,
// con su motivo, el detalle técnico y un botón para reintentar.
const SONDEO_MS = 5000;

const horaCR = (ms) => {
  try {
    return new Date(ms).toLocaleTimeString('es-CR', {
      timeZone: 'America/Costa_Rica', hour: '2-digit', minute: '2-digit', hour12: false,
    });
  } catch (e) {
    return '';
  }
};
const fechaCorta = (iso) => {
  if (!iso) return '';
  const [y, m, d] = iso.split('-').map(Number);
  const meses = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'set', 'oct', 'nov', 'dic'];
  return y && m && d ? `${d} ${meses[m - 1]}` : iso;
};
const plural = (n, uno, varios) => (n === 1 ? uno : varios);

const VACIO = { total: 0, conError: 0, masAntiguo: null, trabajos: [] };

function TrabajoConError({ t, onReintentar, ocupado }) {
  const [abierto, setAbierto] = useState(false);
  const idDetalle = `detalle-${t.id}`;
  return (
    <li className={`rounded-xl border-2 p-3 ${ESTADO_PILL.falla}`}>
      <p className="flex items-start gap-2 text-base font-bold">
        <AlertOctagon size={20} className="mt-0.5 shrink-0" aria-hidden="true" />
        <span>
          No se pudo enviar la inspección del {fechaCorta(t.fecha)}{t.guia ? ` (${t.guia})` : ''}.
        </span>
      </p>
      <p className="mt-1 text-base text-foreground">{t.ultimoError?.motivo || 'Motivo desconocido.'}</p>
      {abierto && (
        <div id={idDetalle} className="mt-2 rounded-lg bg-white p-2 text-sm text-foreground">
          <p>{t.intentos} {plural(t.intentos, 'intento', 'intentos')}{t.ultimoError?.at ? `, el último a las ${horaCR(t.ultimoError.at)}` : ''}.</p>
          {t.ultimoError?.detalle ? <p className="mt-1 break-words font-mono text-xs">{t.ultimoError.detalle}</p> : null}
          <p className="mt-1">Sigue guardada en este teléfono. Si el problema sigue, avise a administración.</p>
        </div>
      )}
      <div className="mt-2 flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => onReintentar(t.id)}
          disabled={ocupado}
          className="min-h-[48px] rounded-xl bg-error px-4 text-base font-semibold text-error-foreground disabled:opacity-60"
        >
          Reintentar
        </button>
        <button
          type="button"
          aria-expanded={abierto}
          aria-controls={idDetalle}
          onClick={() => setAbierto((v) => !v)}
          className="min-h-[48px] rounded-xl border-2 border-border bg-white px-4 text-base font-semibold text-foreground"
        >
          {abierto ? 'Ocultar detalle' : 'Ver detalle'}
        </button>
      </div>
    </li>
  );
}

// puedeEnviar=false: no hay sesión (pantalla de PIN); solo se avisa.
export default function SyncIndicator({ puedeEnviar = true, ayuda = '' }) {
  const [resumen, setResumen] = useState(VACIO);
  const [syncing, setSyncing] = useState(false);
  const vivoRef = useRef(true);

  const refresh = useCallback(async () => {
    try {
      const r = await resumenCola();
      if (vivoRef.current) setResumen(r || VACIO);
    } catch (e) { /* sin IndexedDB: nada que mostrar */ }
  }, []);

  const correr = useCallback(async (fn) => {
    setSyncing(true);
    try {
      await fn();
    } catch (e) { /* el motivo queda en el job */ } finally {
      if (vivoRef.current) setSyncing(false);
      refresh();
    }
  }, [refresh]);

  const sync = useCallback(() => correr(() => flushQueue()), [correr]);
  const reintentar = useCallback((id) => correr(() => reintentarTrabajo(id)), [correr]);

  useEffect(() => {
    vivoRef.current = true;
    refresh();
    if (puedeEnviar) sync(); // intento al abrir
    const onOnline = () => { if (puedeEnviar) sync(); };
    window.addEventListener('online', onOnline);
    window.addEventListener(EVENTO_COLA, refresh);
    const t = setInterval(refresh, SONDEO_MS);
    return () => {
      vivoRef.current = false;
      window.removeEventListener('online', onOnline);
      window.removeEventListener(EVENTO_COLA, refresh);
      clearInterval(t);
    };
  }, [refresh, sync, puedeEnviar]);

  if (!resumen.total) return null;
  const conError = resumen.trabajos.filter((x) => x.permanente);
  const n = resumen.total;

  return (
    <section aria-label="Inspecciones sin enviar" className="space-y-2 text-left">
      <div role="status" className={`rounded-2xl border-2 p-3 ${ESTADO_PILL.alerta}`}>
        <p className="flex items-start gap-2 text-base font-bold text-foreground">
          <CloudOff size={20} className="mt-0.5 shrink-0" aria-hidden="true" />
          <span className="tabular-nums">
            {`${n} ${plural(n, 'inspección sin enviar', 'inspecciones sin enviar')}${
              resumen.masAntiguo ? `, guardada${n === 1 ? '' : 's'} en este teléfono desde las ${horaCR(resumen.masAntiguo)}` : ''
            }.`}
          </span>
        </p>
        {ayuda ? <p className="mt-1 text-base text-foreground">{ayuda}</p> : null}
        {puedeEnviar && n > conError.length ? (
          <button
            type="button"
            onClick={sync}
            disabled={syncing}
            className="mt-2 inline-flex min-h-[48px] items-center gap-2 rounded-xl border-2 border-foreground/30 bg-white px-4 text-base font-semibold text-foreground disabled:opacity-60"
          >
            <RefreshCw size={18} className={syncing ? 'animate-spin' : ''} aria-hidden="true" />
            {syncing ? 'Enviando…' : 'Enviar ahora'}
          </button>
        ) : null}
      </div>
      {puedeEnviar && conError.length > 0 && (
        <ul aria-label="Inspecciones rechazadas" className="space-y-2">
          {conError.map((t) => (
            <TrabajoConError key={t.id} t={t} onReintentar={reintentar} ocupado={syncing} />
          ))}
        </ul>
      )}
    </section>
  );
}
